import { useEffect, useState } from "react";
import { Plus, Loader2, KeyRound, Trash2 } from "lucide-react";
import { toast } from "sonner";
import api, { formatApiError } from "@/lib/api";
import { useAdminAuth } from "@/context/AdminAuthContext";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Switch } from "@/components/ui/switch";
import { AdminHeading, Card, Field, inputCls } from "@/components/admin/ui";
import { ROLE_LABELS } from "@/components/admin/ui";

const ROLE_HELP = {
  super_admin: "Everything, including team logins and activity log",
  editor: "Site content, FAQs and enquiries",
  rates_manager: "Scrap rates only",
  support: "Enquiries only",
};

function CreateForm({ onDone }) {
  const [f, setF] = useState({ name: "", email: "", password: "", role: "editor" });
  const [busy, setBusy] = useState(false);
  const set = (k) => (e) => setF((x) => ({ ...x, [k]: e.target.value }));
  const submit = async (e) => {
    e.preventDefault(); setBusy(true);
    try { await api.post("/admin/users", f); toast.success("Admin created"); onDone(); }
    catch (err) { toast.error(formatApiError(err)); }
    finally { setBusy(false); }
  };
  return (
    <form onSubmit={submit} className="space-y-4" data-testid="admin-user-form">
      <Field label="Full name"><input required minLength={2} value={f.name} onChange={set("name")} className={inputCls} data-testid="admin-user-name" /></Field>
      <Field label="Email (login ID)"><input required type="email" value={f.email} onChange={set("email")} className={inputCls} data-testid="admin-user-email" /></Field>
      <Field label="Password" hint="At least 8 characters. Share it securely."><input required minLength={8} type="text" value={f.password} onChange={set("password")} className={inputCls} data-testid="admin-user-password" /></Field>
      <Field label="Role" hint={ROLE_HELP[f.role]}>
        <select value={f.role} onChange={set("role")} className={inputCls} data-testid="admin-user-role">{Object.entries(ROLE_LABELS).map(([k, l]) => <option key={k} value={k}>{l}</option>)}</select>
      </Field>
      <button disabled={busy} className="btn-leaf w-full" data-testid="admin-user-submit">{busy && <Loader2 size={16} className="animate-spin" />} Create admin</button>
    </form>
  );
}

export default function AdminUsers() {
  const { admin: me } = useAdminAuth();
  const [rows, setRows] = useState([]);
  const [creating, setCreating] = useState(false);
  const load = () => api.get("/admin/users").then((r) => setRows(r.data)).catch((e) => toast.error(formatApiError(e)));
  useEffect(() => { load(); }, []);

  const patch = async (u, body, msg) => {
    try { await api.patch(`/admin/users/${u.id}`, body); toast.success(msg); load(); }
    catch (e) { toast.error(formatApiError(e)); }
  };
  const resetPw = (u) => {
    const pw = window.prompt(`New password for ${u.email} (min 8 characters):`);
    if (pw) patch(u, { password: pw }, "Password reset");
  };
  const remove = async (u) => {
    if (!window.confirm(`Remove ${u.email}?`)) return;
    try { await api.delete(`/admin/users/${u.id}`); toast.success("Removed"); load(); }
    catch (e) { toast.error(formatApiError(e)); }
  };

  return (
    <div data-testid="admin-users">
      <AdminHeading title="Admin users" subtitle="Create team logins and choose what each person can manage.">
        <button onClick={() => setCreating(true)} className="btn-leaf !py-2.5 text-sm" data-testid="admin-user-add-btn"><Plus size={16} /> New admin</button>
      </AdminHeading>
      <div className="grid md:grid-cols-2 gap-3 mb-6">
        {Object.entries(ROLE_HELP).map(([k, v]) => (
          <div key={k} className="text-xs p-3 rounded-xl bg-[var(--paper-2)]"><b>{ROLE_LABELS[k]}</b> — {v}</div>
        ))}
      </div>
      <div className="space-y-3">
        {rows.map((u, i) => (
          <Card key={u.id} className="p-4 flex flex-col md:flex-row md:items-center gap-4" data-testid={`admin-user-row-${i}`}>
            <div className="flex-1 min-w-0">
              <div className="font-semibold">{u.name} {u.id === me.id && <span className="text-xs text-[var(--leaf)]">(you)</span>}</div>
              <div className="text-sm text-[var(--ink-soft)] truncate">{u.email}</div>
              <div className="text-xs text-[var(--ink-soft)] mt-0.5">Last login: {u.last_login_at ? new Date(u.last_login_at).toLocaleString("en-IN") : "never"}</div>
            </div>
            <select value={u.role} onChange={(e) => patch(u, { role: e.target.value }, "Role updated")} disabled={u.id === me.id} className={`${inputCls} md:w-44`} data-testid={`admin-user-role-${i}`}>
              {Object.entries(ROLE_LABELS).map(([k, l]) => <option key={k} value={k}>{l}</option>)}
            </select>
            <label className="flex items-center gap-2 text-sm"><Switch checked={u.active} disabled={u.id === me.id} onCheckedChange={(v) => patch(u, { active: v }, v ? "Enabled" : "Disabled")} data-testid={`admin-user-active-${i}`} /> Active</label>
            <div className="flex gap-1">
              <button onClick={() => resetPw(u)} className="p-2 rounded-lg hover:bg-[var(--leaf-soft)]" aria-label="Reset password" data-testid={`admin-user-reset-${i}`}><KeyRound size={15} /></button>
              {u.id !== me.id && <button onClick={() => remove(u)} className="p-2 rounded-lg text-red-600 hover:bg-red-50" aria-label="Remove" data-testid={`admin-user-delete-${i}`}><Trash2 size={15} /></button>}
            </div>
          </Card>
        ))}
      </div>
      <Dialog open={creating} onOpenChange={setCreating}>
        <DialogContent className="max-w-md" data-testid="admin-user-dialog">
          <DialogHeader><DialogTitle>New admin</DialogTitle></DialogHeader>
          {creating && <CreateForm onDone={() => { setCreating(false); load(); }} />}
        </DialogContent>
      </Dialog>
    </div>
  );
}
